import apiService from "./apiService";
import { ENDPOINTS } from "../Endpoints";
import { uploadFile, UPLOAD_PURPOSES } from "../../utils/directUpload";

/**
 * Admin Service
 * Add all admin-related API calls here
 * Example: To add a new admin API endpoint, just add a method below!
 */
class AdminService {
  /**
   * Get dashboard statistics
   * @param {object} params - Query parameters (date range, etc.)
   * @returns {Promise}
   */
  async getDashboardStats(params = {}) {
    return apiService.get("/admin/dashboard/stats", params);
  }

  /**
   * Get dashboard overview totals
   * @param {string} month - Optional month in YYYY-MM format
   * @returns {Promise}
   */
  async getDashboardOverview(month) {
    const params = month ? { month } : {};
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_OVERVIEW, params);
  }

  /**
   * Get revenue performance chart data
   * @param {number} days - Trailing days to include in the revenue chart
   * @returns {Promise}
   */
  async getRevenuePerformance(days = 7) {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_REVENUE_PERFORMANCE, { days });
  }

  /**
   * Get financial summary totals
   * @returns {Promise}
   */
  async getFinancialSummary() {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_FINANCIAL_SUMMARY);
  }

  /**
   * Get revenue trends for last 7 days and last 12 months
   * @returns {Promise}
   */
  async getRevenueTrends() {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_REVENUE_TRENDS);
  }

  /**
   * Get paginated transactions list
   * @param {object} params - Query parameters (search, sortBy, page, limit)
   * @returns {Promise}
   */
  async getDashboardTransactions(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_TRANSACTIONS, params);
  }

  /**
   * Get assistant withdrawals, newest first. Oversight only — payouts are sent
   * automatically, so nothing here is waiting on an admin.
   * @param {object} params - Query parameters (status, page, limit)
   * @returns {Promise}
   */
  async getWithdrawals(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.WITHDRAWALS, params);
  }

  /**
   * Take a payout request on: it moves to "being processed" while the admin
   * sends the money from the bank.
   * @param {string} withdrawalId - Withdrawal id or WD_ reference
   * @returns {Promise}
   */
  async approveWithdrawal(withdrawalId) {
    return apiService.post(
      `${ENDPOINTS.ADMIN.WITHDRAWAL_ACTION}/${withdrawalId}/approve`
    );
  }

  /**
   * Record that the transfer was actually sent.
   * @param {string} withdrawalId - Withdrawal id or WD_ reference
   * @param {string} paymentReference - Bank reference, for tracing later
   * @returns {Promise}
   */
  async markWithdrawalPaid(withdrawalId, paymentReference = "") {
    return apiService.post(
      `${ENDPOINTS.ADMIN.WITHDRAWAL_ACTION}/${withdrawalId}/mark-paid`,
      { paymentReference }
    );
  }

  /**
   * Decline a payout request. The money goes back to the assistant's wallet.
   * @param {string} withdrawalId - Withdrawal id or WD_ reference
   * @param {string} reason - Shown to the assistant
   * @returns {Promise}
   */
  async declineWithdrawal(withdrawalId, reason) {
    return apiService.post(
      `${ENDPOINTS.ADMIN.WITHDRAWAL_ACTION}/${withdrawalId}/decline`,
      { reason }
    );
  }

  /**
   * Undo the last decision on a payout request. Reverting a declined request
   * takes the refunded money back out of the assistant's wallet, so it fails
   * if she has already spent it.
   * @param {string} withdrawalId - Withdrawal id or WD_ reference
   * @param {string} reason - Why the previous decision was wrong
   * @returns {Promise}
   */
  async revertWithdrawal(withdrawalId, reason) {
    return apiService.post(
      `${ENDPOINTS.ADMIN.WITHDRAWAL_ACTION}/${withdrawalId}/revert`,
      { reason }
    );
  }

  /**
   * Travellers owed money after a cancellation.
   * @param {object} params - Query parameters (status, page, limit)
   * @returns {Promise}
   */
  async getRefunds(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.REFUNDS, params);
  }

  /** Reverse the original charge through Paystack. */
  async approveRefund(refundId) {
    return apiService.post(`${ENDPOINTS.ADMIN.REFUND_ACTION}/${refundId}/approve`);
  }

  /** Record a refund returned by hand, outside Paystack. */
  async markRefundPaid(refundId, note = "") {
    return apiService.post(`${ENDPOINTS.ADMIN.REFUND_ACTION}/${refundId}/mark-paid`, {
      note,
    });
  }

  /** Refuse a refund. The traveller is told why. */
  async declineRefund(refundId, reason) {
    return apiService.post(`${ENDPOINTS.ADMIN.REFUND_ACTION}/${refundId}/decline`, {
      reason,
    });
  }

  /** Undo the last decision on a refund. */
  async revertRefund(refundId, reason) {
    return apiService.post(`${ENDPOINTS.ADMIN.REFUND_ACTION}/${refundId}/revert`, {
      reason,
    });
  }

  /**
   * Get providers aggregate metrics
   * @returns {Promise}
   */
  async getDashboardProviders() {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_PROVIDERS);
  }

  /**
   * Get provider operations overview totals
   * @param {string} month - Optional month in YYYY-MM format
   * @returns {Promise}
   */
  async getProvidersOverview(month) {
    const params = month ? { month } : {};
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_PROVIDERS_OVERVIEW, params);
  }

  /**
   * Get paginated providers directory
   * @param {object} params - Query parameters (status, search, page, limit)
   * @returns {Promise}
   */
  async getProvidersDirectory(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_PROVIDERS_DIRECTORY, params);
  }

  /**
   * Get full provider details
   * @param {string} providerId - Provider user ID
   * @returns {Promise}
   */
  async getProviderDetails(providerId) {
    return apiService.get(`${ENDPOINTS.ADMIN.DASHBOARD_PROVIDER_DETAIL}/${providerId}`);
  }

  /**
   * Download dashboard report
   * @param {object} params - Query parameters (month, format)
   * @returns {Promise}
   */
  async downloadDashboardReport(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_REPORT_DOWNLOAD, params, {
      responseType: "blob",
    });
  }

  /**
   * Download providers insights report
   * @param {object} params - Query parameters (month, status, search, page, limit, format)
   * @returns {Promise}
   */
  async downloadProvidersInsights(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.DASHBOARD_PROVIDERS_INSIGHTS_DOWNLOAD, params, {
      responseType: "blob",
    });
  }

  /**
   * Get urgent attention items
   * @returns {Promise}
   */
  async getUrgentAttention() {
    return apiService.get("/admin/dashboard/urgent");
  }

  /**
   * Get all assistants
   * @param {object} params - Query parameters (page, limit, filters)
   * @returns {Promise}
   */
  async getAssistants(params = {}) {
    return apiService.get("/admin/assistants", params);
  }

  /**
   * Get assistant by ID
   * @param {string} id - Assistant ID
   * @returns {Promise}
   */
  async getAssistantById(id) {
    return apiService.get(`/admin/assistants/${id}`);
  }

  /**
   * Update assistant status
   * @param {string} id - Assistant ID
   * @param {object} data - Update data
   * @returns {Promise}
   */
  async updateAssistant(id, data) {
    return apiService.put(`/admin/assistants/${id}`, data);
  }

  /**
   * Get all accommodations
   * @param {object} params - Query parameters
   * @returns {Promise}
   */
  async getAccommodations(params = {}) {
    return apiService.get("/admin/accommodations", params);
  }

  /**
   * Get accommodation by ID
   * @param {string} id - Accommodation ID
   * @returns {Promise}
   */
  async getAccommodationById(id) {
    return apiService.get(`/admin/accommodations/${id}`);
  }

  /**
   * Update accommodation
   * @param {string} id - Accommodation ID
   * @param {object} data - Update data
   * @returns {Promise}
   */
  async updateAccommodation(id, data) {
    return apiService.put(`/admin/accommodations/${id}`, data);
  }

  /**
   * Get all rides
   * @param {object} params - Query parameters
   * @returns {Promise}
   */
  async getRides(params = {}) {
    return apiService.get("/admin/rides", params);
  }

  /**
   * Get ride by ID
   * @param {string} id - Ride ID
   * @returns {Promise}
   */
  async getRideById(id) {
    return apiService.get(`/admin/rides/${id}`);
  }

  /**
   * Resolve ride dispute
   * @param {string} id - Dispute ID
   * @param {object} resolution - Resolution data
   * @returns {Promise}
   */
  async resolveRideDispute(id, resolution) {
    return apiService.post(`/admin/rides/disputes/${id}/resolve`, resolution);
  }

  /**
   * Get financial reports
   * @param {object} params - Query parameters (date range, type, etc.)
   * @returns {Promise}
   */
  async getFinancialReports(params = {}) {
    return apiService.get("/admin/finances/reports", params);
  }

  /**
   * Download report
   * @param {string} reportType - Type of report
   * @param {object} params - Report parameters
   * @returns {Promise}
   */
  async downloadReport(reportType, params = {}) {
    return apiService.get(`/admin/reports/${reportType}`, params, {
      responseType: "blob",
    });
  }

  /**
   * Get system settings
   * @returns {Promise}
   */
  async getSettings() {
    return apiService.get("/admin/settings");
  }

  /**
   * Update system settings
   * @param {object} settings - Settings data
   * @returns {Promise}
   */
  async updateSettings(settings) {
    return apiService.put("/admin/settings", settings);
  }

  /**
   * Upload a new platform logo. Persists the resulting URL on
   * platformConfiguration.logoUrl and returns the updated settings.
   * @param {File} file - Image file selected in the settings page
   * @returns {Promise}
   */
  async uploadSettingsLogo(file, options = {}) {
    // Uploaded from the browser; the API verifies the asset with Cloudinary
    // before persisting the URL.
    const { publicId } = await uploadFile(
      file,
      UPLOAD_PURPOSES.PLATFORM_LOGO,
      options
    );
    return apiService.post(ENDPOINTS.ADMIN.SETTINGS_LOGO, { publicId });
  }

  /**
   * Review onboarding provider profile
   * @param {string} providerId - Provider user ID
   * @param {object} payload - Review outcome data ({ status, feedback })
   * @returns {Promise}
   */
  async reviewProviderProfile(providerId, payload = {}) {
    if (!providerId) {
      throw new Error("Provider ID is required.");
    }
    return apiService.post(
      `${ENDPOINTS.ADMIN.PROVIDERS_REVIEW}/${providerId}/review`,
      payload
    );
  }

  /**
   * Approve or reject edits submitted by an already-approved assistant. This
   * never changes their account status — they stay live either way.
   */
  async reviewProviderChanges(providerId, payload = {}) {
    if (!providerId) {
      throw new Error("Provider ID is required.");
    }
    return apiService.post(
      `${ENDPOINTS.ADMIN.PROVIDERS_REVIEW}/${providerId}/changes/review`,
      payload
    );
  }

  /**
   * Suspend a provider
   * @param {string} providerId - Provider user ID
   * @returns {Promise}
   */
  async suspendProvider(providerId) {
    if (!providerId) {
      throw new Error("Provider ID is required.");
    }
    return apiService.post(`${ENDPOINTS.ADMIN.PROVIDERS_REVIEW}/${providerId}/suspend`);
  }

  /**
   * Release an assistant payout held on a trip that never got both
   * confirmations. Settles whatever the booking still owes and closes it.
   * @param {string} bookingId
   * @param {{ reason?: string }} payload
   */
  async releaseBookingPayout(bookingId, payload = {}) {
    if (!bookingId) {
      throw new Error("Booking ID is required.");
    }
    return apiService.post(
      `${ENDPOINTS.ADMIN.BOOKINGS}/${bookingId}/release-payout`,
      payload
    );
  }

  /**
   * Unsuspend a provider
   * @param {string} providerId - Provider user ID
   * @returns {Promise}
   */
  async unsuspendProvider(providerId) {
    if (!providerId) {
      throw new Error("Provider ID is required.");
    }
    return apiService.post(`${ENDPOINTS.ADMIN.PROVIDERS_REVIEW}/${providerId}/unsuspend`);
  }

  /**
   * Delete a provider
   * @param {string} providerId - Provider user ID
   * @returns {Promise}
   */
  async deleteProvider(providerId) {
    if (!providerId) {
      throw new Error("Provider ID is required.");
    }
    return apiService.delete(`${ENDPOINTS.ADMIN.PROVIDERS_REVIEW}/${providerId}`);
  }

  /**
   * Get paginated users list
   * @param {object} params - Query parameters (search, page, limit)
   * @returns {Promise}
   */
  async getUsers(params = {}) {
    return apiService.get(ENDPOINTS.ADMIN.USERS, params);
  }

  /**
   * Get user details by ID
   * @param {string} userId - User ID
   * @returns {Promise}
   */
  async getUserById(userId) {
    return apiService.get(`${ENDPOINTS.ADMIN.USERS_DETAIL}/${userId}`);
  }

  /**
   * Suspend a user
   * @param {string} userId - User ID
   * @returns {Promise}
   */
  async suspendUser(userId) {
    if (!userId) {
      throw new Error("User ID is required.");
    }
    return apiService.post(`${ENDPOINTS.ADMIN.USERS_SUSPEND}/${userId}/suspend`);
  }

  /**
   * Unsuspend a user
   * @param {string} userId - User ID
   * @returns {Promise}
   */
  async unsuspendUser(userId) {
    if (!userId) {
      throw new Error("User ID is required.");
    }
    return apiService.post(`${ENDPOINTS.ADMIN.USERS_UNSUSPEND}/${userId}/unsuspend`);
  }
}

// Create and export a singleton instance
const adminService = new AdminService();

export default adminService;
